import { WorkflowBuilder } from "~/organisms/WorkflowBuilder";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useWorkflow } from "~/api/v2/useWorkflow";
import { OverlaySpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { type WorkflowStepInput, type WorkflowTemplate } from "~/api/types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  putWorkflow,
  type PutWorkflowData,
  applyWorkflowTemplate,
} from "~/api/v2/requests";
import { type ChangeEvent, useMemo, useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import useSWR from "swr";
import { Button } from "~/atoms/button";
import DatePicker from "~/molecules/DatePicker";
import { Card, CardContent, CardFooter } from "~/atoms/card";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { DocumentBreadcrumbs } from "~/molecules/DocumentBreadcrumbs";
import { toISOString, formatDate } from "~/helpers/date";
import { Textarea } from "~/atoms/textarea";
import HeadingPage from "~/molecules/HeadingPage";
import { canEditWorkflow, isDraftVersion } from "~/helpers/documentPermissions";
import { Label } from "~/atoms/label";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { useWorkflowTemplates } from "~/api/v2/useWorkflowTemplates";
import { useWorkflowUsers } from "~/hooks/useWorkflowUsers";
import { PlatformRole } from "~/config/types";
import { simpleFetcher } from "~/api/fetcher";
import { useUserContext } from "~/hooks/useUserContext";
import { featureFlags } from "~/config/featureFlags";
import { mapStepInputsToWorkflowSteps, addStepKeys } from "~/helpers/workflow";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";

interface DescriptionFieldProps {
  isPreviewMode: boolean;
  isEditMode: boolean;
  description: string | null;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  noDescriptionLabel: string;
}

function DescriptionField({
  isPreviewMode,
  isEditMode,
  description,
  onChange,
  noDescriptionLabel,
}: DescriptionFieldProps) {
  if (isPreviewMode) {
    return <Textarea value={description ?? ""} disabled className="h-52" />;
  }
  if (isEditMode) {
    return (
      <Textarea
        value={description ?? ""}
        onChange={onChange}
        className="h-52"
      />
    );
  }
  if (description) {
    return (
      <pre className="font-sans text-sm whitespace-pre-wrap">{description}</pre>
    );
  }
  return <p>{noDescriptionLabel}</p>;
}

interface Props {
  isEditMode?: boolean;
}

export default function WorkflowPage({ isEditMode = false }: Props) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();

  const canManagePlatform =
    user.role === PlatformRole.coordinator ||
    user.role === PlatformRole.superAdmin;
  const isWorkflowTemplatesEnabled = useFeatureFlagEnabled(
    featureFlags.workflowTemplates,
  );

  const { data: workflow, isLoading, mutate } = useWorkflow(id);

  const reviewTo = useMemo(
    () =>
      workflow?.documentVersion.reviewTo
        ? new Date(workflow.documentVersion.reviewTo)
        : null,
    [workflow],
  );

  const [editedSteps, setEditedSteps] = useState<WorkflowStepInput[]>([]);
  const [editedDueDate, setEditedDueDate] = useState<Date | null>(null);
  const [editedDescription, setEditedDescription] = useState<string | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastWorkflowId, setLastWorkflowId] = useState<number | null>(null);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [isApplying, setIsApplying] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const { data: templates } = useWorkflowTemplates();
  const { hydrateSteps } = useWorkflowUsers();

  const templateDetailUrl = selectedTemplateId
    ? `/api/v2/workflow-templates/${selectedTemplateId}`
    : null;
  const { data: templateDetail } = useSWR<WorkflowTemplate>(
    templateDetailUrl,
    simpleFetcher,
  );

  const isPreviewMode = selectedTemplateId != null && templateDetail != null;

  const previewSteps = useMemo(
    () => (templateDetail ? hydrateSteps(templateDetail) : []),
    [templateDetail, hydrateSteps],
  );

  const templateOptions = useMemo(
    () =>
      (templates ?? []).map((tmpl) => ({
        value: String(tmpl.id),
        label: tmpl.name ?? t("workflowTemplates.defaultBadge"),
      })),
    [templates, t],
  );

  if (workflow && workflow.id !== lastWorkflowId) {
    setLastWorkflowId(workflow.id);
    if (isEditMode) {
      setEditedSteps(addStepKeys(workflow.steps));
      setEditedDueDate(reviewTo);
      setEditedDescription(workflow.description);
      setHasUnsavedChanges(false);
    }
  }

  if (isLoading) return <OverlaySpinner />;
  if (!workflow) return <NoResult />;

  const isDraft = isDraftVersion(workflow.documentVersion);

  if (isEditMode && !isDraft) {
    return <Navigate to={`/workflows/${workflow.id}`} replace />;
  }

  const previewWorkflow = isPreviewMode
    ? {
        ...workflow,
        isOngoing: false,
        steps: mapStepInputsToWorkflowSteps(previewSteps),
        description: templateDetail.description,
        stepInstances: [],
      }
    : null;

  const handleSave = async () => {
    const invalidStep = editedSteps.find((step) => !step.name.trim());

    if (invalidStep) {
      toast.error(t("commons.workflow.allStepsMustHaveName"));
      return;
    }

    const data: PutWorkflowData = {
      reviewTo: editedDueDate,
      description: editedDescription,
      steps: editedSteps.map((step) => ({
        id: step.id,
        name: step.name.trim(),
        description: step.description ?? null,
        type: step.type,
        dueAt: step.dueAt ? toISOString(step.dueAt) : null,
        users: step.users.map((user) => ({
          userId: user.userId,
          permission: user.permission,
        })),
      })),
    };

    try {
      await putWorkflow(workflow.id, data);

      setHasUnsavedChanges(false);
      toast.success(t("commons.workflow.workflowUpdatedSuccessfully"));
      void mutate();

      setTimeout(() => void navigate(-1), 10);
    } catch (error) {
      console.error(t("commons.workflow.failedToUpdateWorkflow"), error);
      toast.error(t("commons.workflow.failedToUpdateWorkflow"));
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) return;

    setIsApplying(true);
    try {
      await applyWorkflowTemplate(Number(selectedTemplateId), [
        workflow.documentVersion.id,
      ]);
      toast.success(t("workflowTemplates.applyOnWorkflow.success"));
      setSelectedTemplateId(null);

      const updatedWorkflow = await mutate();
      if (updatedWorkflow) {
        const newReviewTo = updatedWorkflow.documentVersion.reviewTo
          ? new Date(updatedWorkflow.documentVersion.reviewTo)
          : null;
        setEditedSteps(addStepKeys(updatedWorkflow.steps));
        setEditedDueDate(newReviewTo);
        setEditedDescription(updatedWorkflow.description);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Failed to apply template", error);
      toast.error(t("workflowTemplates.applyOnWorkflow.error"));
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancelPreview = () => {
    setSelectedTemplateId(null);
  };

  const handleTemplateSelect = (value: string) => {
    setSelectedTemplateId(value === selectedTemplateId ? null : value);
  };

  const handleDueDateChange = (date?: Date) => {
    setEditedDueDate(date ?? null);
    setHasUnsavedChanges(true);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setEditedDescription(e.target.value);
    setHasUnsavedChanges(true);
  };

  const breadcrumbs = [
    {
      label: t("commons.workflows.workflow", { count: 1 }),
      to: isEditMode ? `/workflows/${workflow.id}` : undefined,
    },
    ...(isEditMode ? [{ label: t("commons.workflows.edit") }] : []),
  ];

  const canEditCurrentWorkflow = canEditWorkflow(workflow.documentVersion);

  const showTemplateDropdown =
    isEditMode &&
    canEditCurrentWorkflow &&
    !workflow.isOngoing &&
    canManagePlatform &&
    isWorkflowTemplatesEnabled;

  let displaySteps: WorkflowStepInput[];
  if (isPreviewMode) {
    displaySteps = previewSteps;
  } else if (isEditMode) {
    displaySteps = editedSteps;
  } else {
    displaySteps = addStepKeys(workflow.steps);
  }

  const displayWorkflow = isPreviewMode ? previewWorkflow! : workflow;

  const displayEditMode = isEditMode && !isPreviewMode;

  let displayDescription: string | null;
  if (isPreviewMode) {
    displayDescription = templateDetail.description;
  } else if (isEditMode) {
    displayDescription = editedDescription;
  } else {
    displayDescription = workflow.description;
  }

  const displayDueDate = isEditMode ? editedDueDate : reviewTo;

  return (
    <div className="container flex grow flex-col gap-6 py-6">
      <DocumentBreadcrumbs
        documentId={workflow.document.id}
        versionId={workflow.documentVersion.id}
        title={workflow.documentVersion.title}
        additionalBreadcrumbs={breadcrumbs}
      />

      <div className="flex items-center justify-between">
        <HeadingPage
          title={
            isEditMode
              ? t("commons.workflows.edit")
              : t("commons.workflows.workflow", { count: 1 })
          }
        />
        <div className="flex items-center gap-2">
          {isPreviewMode && (
            <>
              <Button
                variant="outline"
                onClick={handleCancelPreview}
                disabled={isApplying}
              >
                {t("commons.buttons.cancel")}
              </Button>
              <Button
                onClick={
                  hasUnsavedChanges
                    ? () => setShowApplyConfirm(true)
                    : handleApplyTemplate
                }
                disabled={isApplying}
              >
                {isApplying
                  ? t("workflowTemplates.applying")
                  : t("workflowTemplates.applyOnWorkflow.apply")}
              </Button>
            </>
          )}
          {showTemplateDropdown && (
            <div className="w-[220px]">
              <ControlledCombobox
                value={selectedTemplateId}
                options={templateOptions}
                placeholder={t(
                  "workflowTemplates.applyOnWorkflow.selectPlaceholder",
                )}
                showSearch={templateOptions.length > 5}
                onChange={handleTemplateSelect}
                disabled={isApplying}
              />
            </div>
          )}
          {!isEditMode && canEditCurrentWorkflow && !isPreviewMode && (
            <Button variant="outline" asChild>
              <Link to={`/workflows/${workflow.id}/edit`}>
                {t("commons.workflow.editWorkflow")}
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-6 pt-6">
          <div
            className={
              isPreviewMode
                ? "flex flex-col gap-6 rounded-lg border-2 border-dashed border-stone-300 p-4"
                : "flex flex-col gap-6"
            }
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-base font-semibold">
                  {t("commons.workflow.steps")}
                </Label>
                {workflow.workflowTemplateId != null && (
                  <p className="text-muted-foreground text-sm">
                    {t("commons.workflow.createdFromTemplate", {
                      name:
                        workflow.workflowTemplateName ??
                        t("workflowTemplates.defaultBadge"),
                    })}
                  </p>
                )}
              </div>
              <p className="text-muted-foreground text-sm">
                {t("commons.workflow.stepsDescription")}
              </p>
              <WorkflowBuilder
                isEditMode={displayEditMode}
                workflow={displayWorkflow}
                steps={displaySteps}
                setSteps={setEditedSteps}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-base font-semibold">
                {t("commons.workflow.description")}
              </Label>
              <p className="text-muted-foreground text-sm">
                {t("commons.workflow.descriptionDescription")}
              </p>
              <DescriptionField
                isPreviewMode={isPreviewMode}
                isEditMode={isEditMode}
                description={displayDescription}
                onChange={handleDescriptionChange}
                noDescriptionLabel={t("commons.workflow.noDescription")}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-base font-semibold">
              {t("commons.workflow.dueDate")}
            </Label>
            <p className="text-muted-foreground text-sm">
              {t("commons.workflow.dueDateDescription")}
            </p>
            {isEditMode && !isPreviewMode ? (
              <DatePicker
                value={editedDueDate}
                onChange={handleDueDateChange}
                minDate={new Date()}
              />
            ) : isPreviewMode ? (
              <DatePicker value={displayDueDate} disabled />
            ) : (
              <p>
                {reviewTo
                  ? formatDate(reviewTo)
                  : t("commons.workflow.noDueDate")}
              </p>
            )}
          </div>
        </CardContent>

        {isEditMode && !isPreviewMode && (
          <CardFooter className="justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t("commons.workflow.cancel")}
            </Button>
            <Button disabled={!hasUnsavedChanges} onClick={handleSave}>
              {t("commons.workflow.saveChanges")}
            </Button>
          </CardFooter>
        )}
      </Card>

      <UnsavedChangesAlert
        hasUnsavedChanges={isEditMode && hasUnsavedChanges && !isPreviewMode}
        title={t("commons.unsavedChanges.workflow.title")}
        description={t("commons.unsavedChanges.workflow.description")}
        cancelLabel={t("commons.unsavedChanges.continueEditing")}
        confirmLabel={t("commons.unsavedChanges.discardChanges")}
      />

      <ConfirmDialog
        open={showApplyConfirm}
        onOpenChange={setShowApplyConfirm}
        title={t("workflowTemplates.applyOnWorkflow.unsavedChangesTitle")}
        description={t(
          "workflowTemplates.applyOnWorkflow.unsavedChangesDescription",
        )}
        confirmLabel={t(
          "workflowTemplates.applyOnWorkflow.unsavedChangesConfirm",
        )}
        onConfirm={handleApplyTemplate}
      />
    </div>
  );
}
